import { useState } from 'react';

import { CheckIcon, CopyIcon, InfoIcon, RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/ui/custom/StatusDot';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmailIntegration } from '@/shared/types/integration';

interface DNSRecord {
  id: string;
  status: 'verified' | 'unverified';
  type: string;
  host: string;
  value: string;
}

interface DNSConfigurationProps {
  integration: EmailIntegration;
  onVerify?: () => void;
  isVerifyLoading?: boolean;
  /** Whether the user has write permissions */
  canWrite?: boolean;
}

/**
 * EmailDNSConfiguration - DNS configuration component for email integrations
 *
 * Features:
 * - DNS status header with configuration status
 * - DNS records table with copy functionality
 * - Verify button for DNS record validation
 *
 * @param {EmailIntegration} integration - Email integration to display
 * @returns {JSX.Element} The DNS configuration component
 */
export const DNSConfiguration = ({
  integration,
  canWrite = true,
  onVerify,
  isVerifyLoading = false,
}: DNSConfigurationProps) => {
  const { t } = useTranslation();

  // DNS record keys to map
  const dnsRecordKeys = ['mail_cname', 'dkim1', 'dkim2'] as const;

  // Transform DNS records from integration config to display format
  const displayRecords: DNSRecord[] = dnsRecordKeys
    .map((key) => {
      const dnsRecord = integration?.config?.dns?.[key];
      if (!dnsRecord) {
        return null;
      }
      return {
        id: key,
        status: dnsRecord.valid ? 'verified' : 'unverified',
        type: dnsRecord.type?.toUpperCase(),
        host: dnsRecord.host,
        value: dnsRecord.data,
      } as DNSRecord;
    })
    .filter((record): record is DNSRecord => record !== null);

  const allVerified =
    displayRecords.length > 0 && displayRecords.every((record) => record.status === 'verified');

  const verifyRowSpan = Math.max(displayRecords.length || 0, 3);

  /**
   * Copy button component for InputGroup
   * @param {Object} props - Component props
   * @param {string} props.value - Text value to copy
   */
  const CopyButtonInputGroup = ({ value }: { value: string }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    };

    return (
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          type="button"
          size="icon-sm"
          onClick={handleCopy}
          aria-label={t('common.copy')}
          className="min-w-0"
        >
          {copied ? (
            <CheckIcon className="text-foreground-muted" size={16} />
          ) : (
            <CopyIcon className="text-foreground-muted" size={16} />
          )}
        </InputGroupButton>
      </InputGroupAddon>
    );
  };

  return (
    <div className="relative flex w-full flex-col px-6 pt-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">{t('email.dns_status')}</h3>
          <Badge
            type="outline"
            flavour={allVerified ? 'successful' : 'warning'}
            leadingIcon={<InfoIcon />}
          >
            {allVerified ? t('email.configured') : t('email.needs_configuration')}
          </Badge>
        </div>
        <p className="text-sm text-foreground-muted">{t('email.dns_description')}</p>
      </div>

      <div className="mt-3 overflow-x-auto">
        <Table className="rounded-none border-0 **:data-[slot=table]:bg-transparent">
          <TableHeader className="`[&_th]:bg-transparent! **:data-[slot=table-head]:bg-transparent!">
            <TableRow className="h-auto border-0 hover:bg-transparent">
              <TableHead className="max-w-32 min-w-20 leading-6 font-medium text-foreground-muted">
                {t('email.status')}
              </TableHead>
              <TableHead className="max-w-24 min-w-15 leading-6 font-medium text-foreground-muted">
                {t('email.type')}
              </TableHead>
              <TableHead className="min-w-30 leading-6 font-medium text-foreground-muted">
                {t('email.host')}
              </TableHead>
              <TableHead className="min-w-30 leading-6 font-medium text-foreground-muted">
                {t('email.value')}
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>

          <TableBody>
            {displayRecords.length > 0
              ? displayRecords.map((record, idx) => (
                  <TableRow key={record.id} className="h-auto border-0 hover:bg-transparent">
                    <TableCell className="max-w-32 min-w-20 p-2 align-middle whitespace-nowrap">
                      <div className="flex items-center gap-1 py-3">
                        <StatusDot
                          aria-hidden="true"
                          variant={record.status === 'verified' ? 'successful' : 'neutral'}
                        />
                        <span className="text-sm text-foreground-muted">
                          {t(`email.${record.status}`)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-24 min-w-15 p-2 align-middle whitespace-nowrap">
                      <div className="flex items-center py-3">
                        <span className="text-sm text-foreground-muted">{record.type}</span>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-30 p-2 align-middle">
                      <div className="w-full py-3">
                        <InputGroup size="md" className="w-full">
                          <InputGroupInput
                            type="text"
                            value={record.host}
                            readOnly
                            className="text-sm"
                          />
                          <CopyButtonInputGroup value={record.host} />
                        </InputGroup>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-30 p-2 align-middle">
                      <div className="w-full py-3">
                        <InputGroup size="md" className="w-full">
                          <InputGroupInput
                            type="text"
                            value={record.value}
                            readOnly
                            className="truncate text-sm"
                          />
                          <CopyButtonInputGroup value={record.value} />
                        </InputGroup>
                      </div>
                    </TableCell>

                    {idx === 0 ? (
                      <TableCell rowSpan={verifyRowSpan} className="p-2 align-middle">
                        <div className="flex h-full items-center justify-end">
                          <Button
                            type="button"
                            disabled={!canWrite}
                            className="ml-auto flex h-10 w-fit items-center justify-center gap-2 whitespace-nowrap"
                            onClick={onVerify}
                            isLoading={isVerifyLoading}
                          >
                            <RefreshCwIcon className="h-4 w-4" />
                            {t('email.verify')}
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              : Array.from({ length: 3 }).map((_, index) => (
                  <TableRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`skeleton-${index}`}
                    className="h-auto border-0 hover:bg-transparent"
                  >
                    <TableCell className="max-w-32 min-w-20 p-2 align-middle whitespace-nowrap">
                      <div className="flex items-center gap-1 py-3">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </TableCell>
                    <TableCell className="max-w-24 min-w-15 p-2 align-middle whitespace-nowrap">
                      <div className="flex items-center py-3">
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </TableCell>
                    <TableCell className="min-w-30 p-2 align-middle">
                      <div className="flex items-center py-3">
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </TableCell>
                    <TableCell className="min-w-30 p-2 align-middle">
                      <div className="flex items-center py-3">
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </TableCell>

                    {index === 0 ? (
                      <TableCell rowSpan={3} className="p-2 align-middle">
                        <div className="flex h-full items-center justify-end">
                          <Button
                            type="button"
                            disabled={!canWrite}
                            className="ml-auto flex h-10 w-fit items-center justify-center gap-2 whitespace-nowrap"
                            onClick={onVerify}
                            isLoading={isVerifyLoading}
                          >
                            <RefreshCwIcon className="h-4 w-4" />
                            {t('email.verify')}
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
